import type { FileUIPart, UIMessage } from "ai";
import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react";
import type { ComponentProps, HTMLAttributes, ReactElement } from "react";
import { createContext, memo, useContext, useEffect, useState } from "react";
import { type CodeHighlighterPlugin, defaultRehypePlugins, Streamdown } from "streamdown";
import { useCodeHighlighterPlugin } from "../../lib/appearance/code-themes";
import { cn } from "../../lib/utils";
import { Attachment, Attachments } from "./attachments";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from "../ui/dialog";
import { ButtonGroup, ButtonGroupText } from "../ui/button-group";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "../ui/tooltip";

export type MessageProps = HTMLAttributes<HTMLDivElement> & {
	from: UIMessage["role"];
};

export const Message = ({ className, from, ...props }: MessageProps) => (
	<div
		className={cn(
			"group flex w-full flex-col gap-2",
			from === "user"
					? "is-user ml-auto max-w-(--message-user-width) justify-end"
					: "is-assistant mx-auto max-w-conversation",
			className,
		)}
		{...props}
	/>
);

export type MessageContentProps = HTMLAttributes<HTMLDivElement>;

export const MessageContent = ({
	children,
	className,
	...props
}: MessageContentProps) => (
	<div
		className={cn(
			// The charter: my messages in a bubble on the right, the agent's replies as free text.
			"flex w-fit max-w-full min-w-0 flex-col gap-2 overflow-hidden type-reply",
			"group-[.is-user]:ml-auto group-[.is-user]:rounded-(--message-user-radius) group-[.is-user]:bg-bubble group-[.is-user]:px-4 group-[.is-user]:py-2.5 group-[.is-user]:text-foreground",
			"group-[.is-assistant]:w-full group-[.is-assistant]:text-foreground",
			className,
		)}
		{...props}
	>
		{children}
	</div>
);

export type MessageActionsProps = ComponentProps<"div">;

export const MessageActions = ({
	className,
	children,
	...props
}: MessageActionsProps) => (
	<div className={cn("flex items-center gap-1", className)} {...props}>
		{children}
	</div>
);

export type MessageActionProps = ComponentProps<typeof Button> & {
	tooltip?: string;
	label?: string;
};

export const MessageAction = ({
	tooltip,
	children,
	label,
	variant = "ghost",
	size = "icon-sm",
	...props
}: MessageActionProps) => {
	const button = (
		<Button size={size} type="button" variant={variant} {...props}>
			{children}
			<span className="sr-only">{label || tooltip}</span>
		</Button>
	);

	if (tooltip) {
		return (
			<TooltipProvider>
				<Tooltip>
					<TooltipTrigger asChild>{button}</TooltipTrigger>
					<TooltipContent>
						<p>{tooltip}</p>
					</TooltipContent>
				</Tooltip>
			</TooltipProvider>
		);
	}

	return button;
};

type MessageBranchContextType = {
	currentBranch: number;
	totalBranches: number;
	goToPrevious: () => void;
	goToNext: () => void;
	branches: ReactElement[];
	setBranches: (branches: ReactElement[]) => void;
};

const MessageBranchContext = createContext<MessageBranchContextType | null>(
	null,
);

const useMessageBranch = () => {
	const context = useContext(MessageBranchContext);

	if (!context) {
		throw new Error(
			"MessageBranch components must be used within MessageBranch",
		);
	}

	return context;
};

export type MessageBranchProps = HTMLAttributes<HTMLDivElement> & {
	defaultBranch?: number;
	onBranchChange?: (branchIndex: number) => void;
};

export const MessageBranch = ({
	defaultBranch = 0,
	onBranchChange,
	className,
	...props
}: MessageBranchProps) => {
	const [currentBranch, setCurrentBranch] = useState(defaultBranch);
	const [branches, setBranches] = useState<ReactElement[]>([]);

	const handleBranchChange = (newBranch: number) => {
		setCurrentBranch(newBranch);
		onBranchChange?.(newBranch);
	};

	const goToPrevious = () => {
		const newBranch =
			currentBranch > 0 ? currentBranch - 1 : branches.length - 1;
		handleBranchChange(newBranch);
	};

	const goToNext = () => {
		const newBranch =
			currentBranch < branches.length - 1 ? currentBranch + 1 : 0;
		handleBranchChange(newBranch);
	};

	const contextValue: MessageBranchContextType = {
		currentBranch,
		totalBranches: branches.length,
		goToPrevious,
		goToNext,
		branches,
		setBranches,
	};

	return (
		<MessageBranchContext.Provider value={contextValue}>
			<div
				className={cn("grid w-full gap-2 [&>div]:pb-0", className)}
				{...props}
			/>
		</MessageBranchContext.Provider>
	);
};

export type MessageBranchContentProps = HTMLAttributes<HTMLDivElement>;

export const MessageBranchContent = ({
	children,
	...props
}: MessageBranchContentProps) => {
	const { currentBranch, setBranches, branches } = useMessageBranch();
	const childrenArray = Array.isArray(children) ? children : [children];

	// Use useEffect to update branches when they change
	useEffect(() => {
		if (branches.length !== childrenArray.length) {
			setBranches(childrenArray);
		}
	}, [childrenArray, branches, setBranches]);

	return childrenArray.map((branch, index) => (
		<div
			className={cn(
				"grid gap-2 overflow-hidden [&>div]:pb-0",
				index === currentBranch ? "block" : "hidden",
			)}
			key={branch.key}
			{...props}
		>
			{branch}
		</div>
	));
};

export type MessageBranchSelectorProps = HTMLAttributes<HTMLDivElement> & {
	from: UIMessage["role"];
};

export const MessageBranchSelector = ({
	className,
	from,
	...props
}: MessageBranchSelectorProps) => {
	const { totalBranches } = useMessageBranch();

	// Don't render if there's only one branch
	if (totalBranches <= 1) {
		return null;
	}

	return (
		<ButtonGroup
			className="[&>*:not(:first-child)]:rounded-l-md [&>*:not(:last-child)]:rounded-r-md"
			orientation="horizontal"
			{...props}
		/>
	);
};

export type MessageBranchPreviousProps = ComponentProps<typeof Button>;

export const MessageBranchPrevious = ({
	children,
	...props
}: MessageBranchPreviousProps) => {
	const { goToPrevious, totalBranches } = useMessageBranch();

	return (
		<Button
			aria-label="Previous branch"
			disabled={totalBranches <= 1}
			onClick={goToPrevious}
			size="icon-sm"
			type="button"
			variant="ghost"
			{...props}
		>
			{children ?? <ChevronLeftIcon size={14} />}
		</Button>
	);
};

export type MessageBranchNextProps = ComponentProps<typeof Button>;

export const MessageBranchNext = ({
	children,
	className,
	...props
}: MessageBranchNextProps) => {
	const { goToNext, totalBranches } = useMessageBranch();

	return (
		<Button
			aria-label="Next branch"
			disabled={totalBranches <= 1}
			onClick={goToNext}
			size="icon-sm"
			type="button"
			variant="ghost"
			{...props}
		>
			{children ?? <ChevronRightIcon size={14} />}
		</Button>
	);
};

export type MessageBranchPageProps = HTMLAttributes<HTMLSpanElement>;

export const MessageBranchPage = ({
	className,
	...props
}: MessageBranchPageProps) => {
	const { currentBranch, totalBranches } = useMessageBranch();

	return (
		<ButtonGroupText
			className={cn(
				"border-none bg-transparent text-muted-foreground shadow-none",
				className,
			)}
			{...props}
		>
			{currentBranch + 1} of {totalBranches}
		</ButtonGroupText>
	);
};

export type MessageResponseProps = ComponentProps<typeof Streamdown>;

/** Streamdown's rehype plugins by name (raw, sanitize, harden): a caller adds its own between. */
export const markdownRehypePlugins: Record<
	string,
	NonNullable<MessageResponseProps["rehypePlugins"]>[number]
> = defaultRehypePlugins;

/**
 * Markdown, rendered as a whole (`static`) unless the caller says it is still streaming:
 * Streamdown's streaming mode commits its blocks in a transition, and a stalled transition
 * left a finished message cut short on screen.
 */
export const MessageResponse = memo(
	({ className, plugins, mode = "static", ...props }: MessageResponseProps) => {
		// The app's code themes; a theme change re-renders through the context despite `memo`.
		const code = useCodeHighlighterPlugin();
		return (
		<Streamdown
			mode={mode}
			className={cn(
					"type-conversation size-full min-w-0 text-foreground [&>*]:my-2 [&>*:first-child]:mt-0 [&>*:last-child]:mb-0 [&_[data-streamdown=blockquote]]:my-2.5 [&_[data-streamdown=blockquote]]:border-border [&_[data-streamdown=blockquote]]:text-muted-foreground [&_[data-streamdown=code-block]]:my-3 [&_[data-streamdown=code-block]]:gap-0 [&_[data-streamdown=code-block]]:overflow-hidden [&_[data-streamdown=code-block]]:rounded-code [&_[data-streamdown=code-block]]:border-0 [&_[data-streamdown=code-block]]:bg-transparent [&_[data-streamdown=code-block]]:p-0 [&_[data-streamdown=code-block-body]]:rounded-code [&_[data-streamdown=code-block-body]]:border [&_[data-streamdown=code-block-body]]:border-hairline [&_[data-streamdown=code-block-body]]:bg-code-surface [&_[data-streamdown=code-block-body]]:p-3 [&_[data-streamdown=code-block-body]]:type-caption [&_[data-streamdown=code-block-body]]:leading-relaxed [&_[data-streamdown=code-block-body]_code>span]:block [&_[data-streamdown=code-block-body]_code>span]:before:hidden [&_[data-streamdown=code-block-header]]:h-6 [&_[data-streamdown=code-block-header]]:px-1 [&_[data-streamdown=code-block-header]]:text-xs [&_[data-streamdown=code-block-header]]:text-muted-foreground/70 [&_[data-streamdown=heading-1]]:mt-5 [&_[data-streamdown=heading-1]]:mb-1.5 [&_[data-streamdown=heading-1]]:text-[1.214em] [&_[data-streamdown=heading-1]]:font-semibold [&_[data-streamdown=heading-1]]:tracking-tight [&_[data-streamdown=heading-1]]:text-foreground [&_[data-streamdown=heading-2]]:mt-4 [&_[data-streamdown=heading-2]]:mb-1.5 [&_[data-streamdown=heading-2]]:text-[1.214em] [&_[data-streamdown=heading-2]]:font-semibold [&_[data-streamdown=heading-2]]:text-foreground [&_[data-streamdown=heading-3]]:mt-3.5 [&_[data-streamdown=heading-3]]:mb-1 [&_[data-streamdown=heading-3]]:text-[1em] [&_[data-streamdown=heading-3]]:font-semibold [&_[data-streamdown=heading-3]]:text-foreground [&_[data-streamdown=inline-code]]:rounded [&_[data-streamdown=inline-code]]:bg-muted/70 [&_[data-streamdown=inline-code]]:px-1.5 [&_[data-streamdown=inline-code]]:py-0.5 [&_[data-streamdown=inline-code]]:text-[0.88em] [&_[data-streamdown=inline-code]]:text-foreground/85 [&_[data-streamdown=link]]:text-reference [&_[data-streamdown=link]]:decoration-reference/55 [&_[data-streamdown=link]]:underline-offset-2 [&_[data-streamdown=link]:hover]:decoration-reference [&_[data-streamdown=strong]]:font-semibold [&_[data-streamdown=strong]]:text-foreground [&_[data-streamdown=unordered-list]]:marker:text-muted-foreground [&_[data-streamdown=ordered-list]]:marker:text-muted-foreground",
				className,
			)}
			controls={{ code: false }}
			plugins={{ ...plugins, code: code as unknown as CodeHighlighterPlugin }}
			{...props}
		/>
		);
	},
	// A message done streaming renders again in its static mode, even with the same text.
	(prevProps, nextProps) =>
		prevProps.children === nextProps.children && prevProps.mode === nextProps.mode,
);

MessageResponse.displayName = "MessageResponse";

export type MessageAttachmentProps = HTMLAttributes<HTMLDivElement> & {
	data: FileUIPart;
	className?: string;
	onRemove?: () => void;
};

export function MessageAttachment({
	data,
	className,
	onRemove,
	...props
}: MessageAttachmentProps) {
	const attachment = (
		<Attachment className={className} data={data} onRemove={onRemove} {...props} />
	);
	if (onRemove || !data.mediaType.startsWith("image/") || !data.url) return attachment;
	// An image opens full size: a screenshot is unreadable as a thumbnail.
	return (
		<ImageLightbox name={data.filename || "Image"} src={data.url}>
			{attachment}
		</ImageLightbox>
	);
}

export type ImageLightboxProps = {
	/** What the image shows, said to assistive technologies and on the trigger. */
	name: string;
	src: string;
	/** The thumbnail (or inline image) that opens the full-size view. */
	children: ReactElement;
	className?: string;
};

/** An image that opens full size in a dialog when clicked (attachments, tool images, replies). */
export function ImageLightbox({ name, src, children, className }: ImageLightboxProps) {
	return (
		<Dialog>
			<DialogTrigger asChild>
				<button
					aria-label={`Open ${name}`}
					className={cn(
						"cursor-zoom-in rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60",
						className,
					)}
					type="button"
				>
					{children}
				</button>
			</DialogTrigger>
			<DialogContent className="w-auto max-w-[calc(100vw-4rem)] p-2 sm:max-w-[calc(100vw-4rem)]">
				<DialogTitle className="sr-only">{name}</DialogTitle>
				<img
					alt={name}
					className="max-h-[calc(100dvh-6rem)] max-w-full rounded-xl object-contain"
					decoding="async"
					src={src}
				/>
			</DialogContent>
		</Dialog>
	);
}

export type MessageAttachmentsProps = ComponentProps<"div">;

export function MessageAttachments({
	children,
	className,
	...props
}: MessageAttachmentsProps) {
	if (!children) {
		return null;
	}

	return <Attachments className={cn("ml-auto w-fit", className)} {...props}>{children}</Attachments>;
}

export type MessageToolbarProps = ComponentProps<"div">;

export const MessageToolbar = ({
	className,
	children,
	...props
}: MessageToolbarProps) => (
	<div
		className={cn(
			"mt-4 flex w-full items-center justify-between gap-4",
			className,
		)}
		{...props}
	>
		{children}
	</div>
);
